import { createContext, type PropsWithChildren, useMemo } from 'react';
import { Configure, InstantSearch } from 'react-instantsearch';
import Client from '@searchkit/instantsearch-client';
import { history } from 'instantsearch.js/es/lib/routers/index.js';

import { type Repub } from '@cx-modules/constants';

import { type BetInsightsConfig } from '~/apps/product-plus/configuration/BetInsightsConfiguration';
import { type BettingInsightsContent, type EsIndex } from '~/apps/product-plus/configuration/types';
import { IGNORED_URL_PARAMS } from '~/apps/product-plus/constants/url-params';
import { type SearchResult } from '~/apps/product-plus/utils/searchClient';

export type InitialSearchResults = { results: SearchResult[] };

export const InitialSearchResultsContext = createContext<InitialSearchResults | undefined>(undefined);

/**
 * Removes the page parameter from the UI state before syncing to the URL.
 * This prevents page numbers from persisting in the URL.
 */
function stateToRoute(uiState: Record<string, any>) {
  return Object.keys(uiState).reduce(
    (acc, indexName) => {
      const indexState = uiState[indexName];
      if (indexState) {
        const { page: _page, ...restState } = indexState;
        acc[indexName] = restState;
      }
      return acc;
    },
    {} as typeof uiState,
  );
}

/**
 * Filters out ignored URL parameters from the route state.
 * This prevents certain query parameters from being synced to the search state.
 */
function routeToState(routeState: Record<string, any>) {
  return Object.fromEntries(Object.entries(routeState).filter(([key]) => !IGNORED_URL_PARAMS.includes(key)));
}

export default function SearchWrapper<
  TIndex extends EsIndex,
  TContent extends BettingInsightsContent<TIndex> = BettingInsightsContent<TIndex>,
>({
  children,
  config,
  hitsPerPage = 10,
  indexName,
  initialResults,
  isRoutingEnabled = true,
  repub,
  state,
  url = '/api/cx/v2/search',
  userTZ,
}: PropsWithChildren<{
  config: BetInsightsConfig<TIndex, TContent>;
  hitsPerPage?: number;
  indexName: TIndex;
  initialResults?: InitialSearchResults;
  isRoutingEnabled?: boolean;
  repub: Repub;
  state: string;
  url?: string;
  userTZ: string;
}>) {
  const usageContextHeader = config?.usageContext ?? 'default';
  const searchClient = useMemo(() => {
    const baseClient = Client({
      headers: {
        'OC-Repub': repub,
        'OC-Selected-State': state,
        'OC-TZ': typeof window !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : userTZ,
        'OC-BI-Usage-Context': usageContextHeader,
      },
      url,
    });

    if (!initialResults) return baseClient;

    let servedInitial = false;
    return {
      ...baseClient,
      // React InstantSearch calls this to fetch results; serve SSR result first
      search(requests: unknown) {
        if (!servedInitial) {
          servedInitial = true;
          return Promise.resolve(initialResults);
        }
        return baseClient.search(requests);
      },
    };
  }, [state, repub, userTZ, url, initialResults, usageContextHeader]);

  const initialUiState = config.ui.initialUiState?.();

  const initialUiStateConfig = initialUiState
    ? {
        initialUiState: {
          [indexName]: initialUiState,
        },
      }
    : undefined;

  const routingConfig =
    !config.isWalled && isRoutingEnabled
      ? {
          routing: {
            router: history({
              // Set to false to adopt future InstantSearch behavior where URL params
              // are preserved when the component unmounts. This prevents the URL from
              // being cleaned up, allowing users to return to their search state via
              // browser back/forward navigation or shared links.
              cleanUrlOnDispose: false,
            }),
            stateMapping: {
              stateToRoute,
              routeToState,
            },
          },
        }
      : undefined;

  return (
    <InstantSearch
      future={{
        preserveSharedStateOnUnmount: true,
      }}
      indexName={indexName}
      searchClient={searchClient}
      {...initialUiStateConfig}
      {...routingConfig}
    >
      <Configure hitsPerPage={hitsPerPage} />
      <InitialSearchResultsContext.Provider value={initialResults}>{children}</InitialSearchResultsContext.Provider>
    </InstantSearch>
  );
}
